import { api } from "@/lib/http.ts";
import { useEffect, useRef, useState } from "react";
import { isSharedThreadPath } from "@/lib/sharedThread.ts";

interface UseReadStateOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number;
  initialValue?: boolean | null;
}

/**
 * Read state tracking hook
 */
export const useReadState = (options: UseReadStateOptions = {}) => {
  const [isRead, setIsRead] = useState(options.initialValue || false);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;

    if (!element) return;

    // don't do all this work if it's already read
    if (isRead) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        setIsRead(isVisible);

        if (isVisible && !isRead) {
          setIsRead(true);
        }
      },
      {
        root: options.root || null,
        rootMargin: options.rootMargin || "0px",
        threshold: options.threshold || 0.25,
      },
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [options.root, options.rootMargin, options.threshold, isRead]);

  return [elementRef, isRead] as const;
};

const BATCH_DELAY = 1000; // ms

export const createBatchManager = (callback: (ids: number[]) => void) => {
  const state = {
    batchSet: new Set<number>(),
    sent: new Set<number>(),
    timeout: undefined as ReturnType<typeof setTimeout> | undefined,
  };

  const processBatch = () => {
    if (state.batchSet.size === 0) return;

    const ids = Array.from(state.batchSet);
    callback(ids);
    state.sent = new Set([...state.sent, ...ids]);
    state.batchSet.clear();
  };

  return {
    add: (id: number) => {
      // no need to resend if a prior batch already sent it
      if (state.sent.has(id)) return;

      state.batchSet.add(id);
      if (state.timeout) {
        clearTimeout(state.timeout);
      }
      state.timeout = setTimeout(processBatch, BATCH_DELAY);
    },
    cleanup: () => {
      if (state.timeout) {
        clearTimeout(state.timeout);
        processBatch();
      }
    },
  };
};

/**
 * singleton to ensure we don't lose state across component mounts, the feed in particular
 * renders multiple items for the same message
 */
const readStateBatch = createBatchManager((ids: number[]) => {
  api.post(`/api/a/messages/read`, { messageIds: ids });
});

/** High level composed hook to handle batched message read state updates */
export const useMarkMessageAsRead = (messageId: number, initialValue: boolean) => {
  const [ref, isRead] = useReadState({ initialValue });

  // ensure we only send the update once
  const sentUpdate = useRef(false);

  useEffect(() => {
    // Skip marking messages as read for shared thread views (unauthenticated users)
    if (isSharedThreadPath()) return;

    if (isRead && !initialValue && !sentUpdate.current) {
      sentUpdate.current = true;
      readStateBatch.add(messageId);
    }
  }, [messageId, isRead, initialValue]);
  useEffect(() => {
    return () => readStateBatch.cleanup();
  }, []);

  return [ref, isRead] as const;
};
